import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import BlogCard from "../components/BlogCard";
import ProductCard from "../components/ProductCard";
import SpecialProduct from "../components/SpecialProduct";
import Container from "../components/Container";
import { useDispatch, useSelector } from "react-redux";
import { getAllBlogs } from "../features/blogs/blogSlice";
import { getAllProducts } from "../features/products/productSlilce";
import { getCategories } from "../features/category/categorySlice";
import { getBrands } from "../features/brand/brandSlice";
import { addToWishlist } from "../features/products/productSlilce";
import moment from "moment";
import ReactStars from "react-rating-stars-component";
import { 
  BsHeart,
  BsStarFill,
  BsArrowRight,
  BsArrowLeft,
  BsSearch,
  BsBag,
  BsPerson,
  BsTelephone,
  BsEnvelope,
  BsGeoAlt,
  BsInstagram,
  BsFacebook,
  BsTwitter,
  BsYoutube,
  BsPinterest,
  BsGift,
  BsPlay,
  BsChevronLeft,
  BsChevronRight
} from "react-icons/bs";
import Meta from "../components/Meta";
import { useTranslation } from "react-i18next";

const Home = () => {
  const { t } = useTranslation();
  const blogState = useSelector((state) => state?.blog?.blog);
  const productState = useSelector((state) => state?.product?.product);
  const categoryState = useSelector((state) => state?.category?.categories);
  const brandState = useSelector((state) => state?.brand?.brands);
  const productLoading = useSelector((state) => state?.product?.isLoading);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentCategorySlide, setCurrentCategorySlide] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const autoplayRef = useRef(null);

  // Hero banner data with modern styling
  const heroBanners = [
    {
      title: "Epic Adventures Await",
      subtitle: "Mythological toys for modern kids",
      description: "Experience the magic of ancient tales through interactive and educational toys.",
      image: "veda-banner-3.png",
      buttonText: "Explore",
      badge: "Trending"
    },
    {
      title: "Divine Toys",
      subtitle: "Mythological toys for modern kids",
      description: "Discover mythological toys that bring ancient stories to life with modern play.",
      image: "/veda-banner-4.png",
      buttonText: "Shop Now",
      badge: "New Arrival"
    },
  ];

  useEffect(() => {
    getblogs();
    getProducts();
    getCategoriesData();
    getBrandsData();
  }, []);

  // Auto-slide with modern approach
  useEffect(() => {
    autoplayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroBanners.length);
    }, 3000);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [heroBanners.length]);

  const handleMouseEnter = () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  };

  const handleMouseLeave = () => {
    autoplayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroBanners.length);
    }, 3000);
  };

  const getblogs = () => {
    dispatch(getAllBlogs());
  };

  const getProducts = () => {
    dispatch(getAllProducts());
  };

  const getCategoriesData = () => {
    dispatch(getCategories());
  };

  const getBrandsData = () => {
    dispatch(getBrands());
  };

  // Debounce function for wishlist operations
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Debounced wishlist function
  const debouncedAddToWish = useCallback(
    debounce((id) => {
      dispatch(addToWishlist(id));
    }, 300),
    []
  );

  const addToWish = (id) => {
    debouncedAddToWish(id);
  };

  // Modern category data with icons
  const fashionCategories = [
    { title: t("header.toys"), icon: "🛍️", color: "red" ,url:'/products'},
    { title: t("header.games"), icon: "🎮", color: "blue", url:"/games" },
    { title: t("header.puzzles"), icon: "🧩", color: "green", url:'/puzzles' },
    { title: t("header.videos"), icon: "🎬", color: "yellow" ,url:'/videos'},
    { title: t("header.virtualTemples"), icon: "🏛️", color: "purple", url:'/virtual-temples'},
  ];
  // Services with modern design
  const services = [
    {
      icon: "🚚",
      title: "Free Shipping",
      description: "On orders over $50"
    },
    {
      icon: "🔄",
      title: "Easy Returns",
      description: "30 day return policy"
    },
    {
      icon: "🛡️",
      title: "Secure Payment",
      description: "100% secure checkout"
    },
    {
      icon: "💬",
      title: "24/7 Support",
      description: "Dedicated support"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroBanners.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroBanners.length) % heroBanners.length);
  };

  // Get unique categories safely
  const uniqueCategories = productState && Array.isArray(productState) 
    ? [...new Set(productState.map(item => item.category).filter(Boolean))]
    : [];

  // Updated testimonials with real Ganesha images
  const testimonials = [
    {
      name: "Avani Vemula",
      text: "It's very comfortable for kids. They can learn and enjoy the slokam. Thanks so much",
      image: "https://m.media-amazon.com/images/I/71d2fe4RAHL.jpg"
    },
    {
      name: "Rajani V Bhat", 
      text: "Really good quality product, my baby liked the most and admires too, thank you panda box and team.",
      image: "/ganesha-review.png"
    },
    {
      name: "Drishti Bharti",
      text: "This Baby Ganesha plush nurtures values and sparks my daughter's curiosity about his stories.",
      image: "https://m.media-amazon.com/images/I/61DPHgCRxqL.jpg"
    }
  ];

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "VEDA - Mythological Toys, Games & Puzzles",
    "url": "https://vedamerchandise.com/",
    "description": "VEDA offers educational and entertaining mythological toys, games, and puzzles for children to learn about Indian culture and values.",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="VEDA - Mythological Toys, Games & Puzzles for Kids"
        description="Discover educational and entertaining mythological toys, games, and puzzles for children to learn about Indian culture and values."
        keywords="mythological toys, Indian mythology, educational toys, kids games, puzzles, VEDA, Hindu mythology, Lord Krishna, Ramayana, Mahabharata"
        author="VEDA Merchandise"
        ogTitle="VEDA - Mythological Toys, Games & Puzzles for Kids"
        ogDescription="Discover educational and entertaining mythological toys, games, and puzzles for children to learn about Indian culture and values."
        ogImage="https://vedamerchandise.com/veda-banner.png"
        ogUrl="https://vedamerchandise.com/"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="VEDA - Mythological Toys, Games & Puzzles for Kids"
        twitterDescription="Discover educational and entertaining mythological toys, games, and puzzles for children to learn about Indian culture and values."
        twitterImage="https://vedamerchandise.com/veda-banner.png"
        robots="index, follow"
        canonicalUrl="https://vedamerchandise.com/"
        schemaMarkup={schemaMarkup}
      />
      
      {/* Modern Bento Grid Hero Section */}
      <section className="w-full px-4 md:px-8 py-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 h-[60vh] md:h-[80vh]">
            
            {/* LEFT: Carousel (3/4 width) */}
            <div 
              className="relative lg:col-span-3  rounded-3xl overflow-hidden shadow-2xl"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {heroBanners.map((banner, index) => (
                <div 
                  key={index} 
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    backgroundImage: ` url(${banner.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Content - Bottom Left */}
                  <div className="absolute bottom-4 left-4 text-white max-w-lg p-4">
                    <span className="inline-block bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                      {banner.badge}
                    </span>
                    <h1 className="text-2xl md:text-4xl font-bold mb-2 drop-shadow-lg leading-tight quicksand">
                      {banner.title}
                    </h1>
                    <p className="text-sm md:text-base opacity-90 drop-shadow-md leading-relaxed mb-4">
                      {banner.subtitle}
                    </p>
                    <Link
                      to="/products"
                      className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg text-sm font-semibold transition-colors duration-300"
                    >
                      <BsBag className="mr-2" /> {banner.buttonText}
                    </Link>
                  </div>
                </div>
              ))}
              
              {/* Navigation arrows */}
              <button 
                onClick={prevSlide}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-2 rounded-full transition-colors z-10"
              >
                <BsChevronLeft className="h-4 w-4" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-2 rounded-full transition-colors z-10"
              >
                <BsChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* RIGHT: Two stacked blocks (1/4 width) */}
            <div className="lg:col-span-1 flex flex-col gap-4 md:gap-6">
              
              {/* Discount Block */}
              <div className="flex-1 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl flex flex-col justify-center items-center text-white p-4 shadow-lg">
                <BsGift className="h-8 w-8 mb-2" />
                <h2 className="text-xl font-bold mb-1 fredoka">Up to 40% Off</h2>
                <p className="text-red-100 mb-3 text-center text-xs">On selected items</p>
                <Link
                  to="/products"
                  className="bg-white text-red-600 hover:bg-gray-100 px-4 py-1.5 rounded-lg font-semibold shadow-md flex items-center gap-1 text-xs transition-colors duration-300"
                >
                  Shop Now
                  <BsArrowRight className="h-3 w-3" />
                </Link>
              </div>

              {/* Video Block */}
              <div className="flex-1 relative rounded-3xl overflow-hidden shadow-lg group">
                <video
                  src="/bannervideo.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                
                {/* Video title overlay */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded text-xs font-medium">
                  Fun with VEDA Toys!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section with Modern Design */}
      <section id="categories" className="py-16 md:py-24 bg-gray-50">
        <Container class1="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-center mb-8 md:mb-12 quicksand">
            <span className="bg-yellow-200 text-gray-800 py-1 px-4 sm:px-6  md:py-4 rounded-full text-sm sm:text-base md:text-3xl">
              {t("home.explore")}
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {fashionCategories.map((category, index) => (
              <Link key={index} to={category.url} className="group">
                <div className="h-full overflow-hiddens transition-all duration-300 hover:shadow-xl hover:-translate-y-2 bg-white border rounded-3xl">
                  <div className="p-6 flex flex-col items-center justify-center text-center">
                    <div className={`p-4 rounded-full mb-4 transition-colors ${
                      category.color === 'red' ? 'bg-red-100 group-hover:bg-red-500' :
                      category.color === 'blue' ? 'bg-blue-100 group-hover:bg-blue-500' :
                      category.color === 'green' ? 'bg-green-100 group-hover:bg-green-500' :
                      category.color === 'purple' ? 'bg-red-100 group-hover:bg-red-500' :
                      'bg-yellow-100 group-hover:bg-yellow-500'
                    }`}>
                      <span className="text-2xl">{category.icon}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 fredoka">{category.title}</h3>
                    <p className="text-sm text-gray-600 flex items-center gap-1 transition-transform group-hover:translate-x-1">
                      Explore now <BsArrowRight className="h-4 w-4" />
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 md:py-24 bg-gray-100">
        <Container class1="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800 quicksand">
            Best Sellers
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productLoading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : productState && Array.isArray(productState) && productState
              .filter(item => item.tags === "bestseller")
              .slice(0, 4)
              .map((item, index) => (
                <div key={index}>
                  <ProductCard data={[item]} />
                </div>
              ))}
          </div>
        </Container>
      </section>

      {/* New Arrivals Section */}
      {(() => {
        const newArrivalProducts = productState && Array.isArray(productState) ? 
          productState.filter(item => item.tags === "newarrival") : [];
        const hasNewArrivalProducts = newArrivalProducts.length > 0;
        
        return hasNewArrivalProducts || productLoading ? (
          <section className="py-16 md:py-24 bg-white">
            <Container class1="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800 quicksand">
                New Arrivals
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {productLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
                      <div className="aspect-square bg-gray-200"></div>
                      <div className="p-4">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))
                ) : newArrivalProducts
                  .slice(0, 4)
                  .map((item, index) => (
                    <div key={index}>
                      <ProductCard data={[item]} />
                    </div>
                  ))}
              </div>
            </Container>
          </section>
        ) : null;
      })()}

      {/* Coming Soon Section */}
      {(() => {
        const comingSoonProducts = productState && Array.isArray(productState) ? 
          productState.filter(item => item.tags === "comingsoon") : [];
        const hasComingSoonProducts = comingSoonProducts.length > 0;
        
        return hasComingSoonProducts || productLoading ? (
          <section className="py-16 md:py-24 bg-gray-100">
            <Container class1="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800 quicksand">
                Coming Soon
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {productLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
                      <div className="aspect-square bg-gray-200"></div>
                      <div className="p-4">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))
                ) : comingSoonProducts
                  .slice(0, 4)
                  .map((item, index) => (
                    <div key={index}>
                      <ProductCard data={[item]} />
                    </div>
                  ))}
              </div>
            </Container>
          </section>
        ) : null;
      })()}

      {/* All Products Showcase with Category Filter */}
      <section className="py-16 md:py-24 bg-gray-50">
        <Container class1="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 quicksand">All Products</h2>
            <p className="text-gray-600 text-lg">Discover our complete collection</p>
          </div>
          
          {/* Category Tabs */}
          <div className="flex justify-center flex-wrap gap-2 mb-8">
            <button 
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                activeCategory === 'all' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setActiveCategory('all')}
            >
              All Products
            </button>
            {uniqueCategories.slice(0, 6).map((cat, index) => (
              <button 
                key={index}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  activeCategory === cat 
                    ? 'bg-red-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          
          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : productState && Array.isArray(productState) && productState
              .filter(item => activeCategory === 'all' || item.category === activeCategory)
              .slice(0, 8)
              .map((item, index) => (
                <div key={index}>
                  <ProductCard data={[item]} />
                </div>
              ))}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/products" 
              className="inline-flex items-center bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-full font-semibold transition-colors duration-300"
            >
              View All Products
            </Link>
          </div>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-16 bg-gray-50">
        <Container class1="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 quicksand">
            <span className="bg-yellow-200 text-gray-800 py-2 px-3 sm:px-4 rounded-full text-md sm:text-base md:text-3xl">
              What People Are Saying
            </span>
          </h2>
          
          {/* Amazon Rating Section */}
          <div className="flex flex-col items-center justify-center mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col md:flex-row items-center gap-6">
              <div className="flex items-center">
                <span className="text-2xl font-bold mr-2">4.8</span>
                <div className="flex">
                  {/* 4 full stars */}
                  <BsStarFill className="h-5 w-5 text-yellow-400" />
                  <BsStarFill className="h-5 w-5 text-yellow-400" />
                  <BsStarFill className="h-5 w-5 text-yellow-400" />
                  <BsStarFill className="h-5 w-5 text-yellow-400" />
                  {/* Half star */}
                  <div className="relative">
                    <BsStarFill className="h-5 w-5 text-gray-300" />
                    <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
                      <BsStarFill className="h-5 w-5 text-yellow-400" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-center md:text-left">
                <p className="font-semibold">Rated on Amazon World Wide.</p>
                <a 
                  href="https://www.amazon.in/Ganesha-Plush-Stuffed-Favourite-Elephant/dp/B0DRNKWLSN" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  See Our Products on Amazon
                </a>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="relative pt-12">
                <div className="bg-white rounded-3xl p-8 text-center shadow-lg pt-16">
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2">
                    <div className="w-24 h-24 rounded-full bg-gray-300 border-4 border-white shadow-md flex items-center justify-center overflow-hidden">
                      <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className="flex justify-center items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <BsStarFill key={i} className="h-5 w-5 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic text-sm mb-4">"{testimonial.text}"</p>
                  <p className="font-bold text-lg">{testimonial.name}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

    </>
  );
};

export default Home;